import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import StatisticsPage, { personPath } from '../../components/statistics/StatisticsPage';
import DataTable, { Column } from '../../components/statistics/DataTable';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { downloadDrilldownCsv, errorMessage, getDrilldown } from '../../services/statisticsService';
import { downloadFile } from '../../utils/csv';
import { formatCount, formatDateTime } from '../../utils/period';

const TITLES: Record<string, string> = {
  signups: 'Sign-ups',
  rides: 'Rides offered',
  requests: 'Seat requests',
  cancellations: 'Cancelled seats',
  captures: 'Ride Captures',
  ratings: 'Ratings',
};

/** Columns that hold a member's id, and so link to them in People. */
const PERSON_COLUMNS = new Set(['userId', 'driverId', 'passengerId']);
const DATE_COLUMNS = new Set(['createdAt', 'respondedAt', 'cancelledAt']);

/** The rows behind a headline figure (PRD §5.8, drill-down). */
const Drilldown = () => {
  const { metric = '' } = useParams();
  const [filters, setFilters] = useStatisticsFilters();
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { data, loading, error, reload } = useStatisticsData(
    (f) => getDrilldown(f, metric, page),
    filters,
    `${metric}|${page}`
  );

  const columns: Column<any>[] = (data?.columns || []).map((c: { key: string; label: string }) => {
    const personLink = PERSON_COLUMNS.has(c.key) || (metric === 'signups' && c.key === 'id');
    return {
      key: c.key,
      label: c.label,
      numeric: c.key.endsWith('Id') || c.key === 'id' || /seats|price|minutes|alerts|overall/i.test(c.key),
      render: personLink
        ? (row: any) => (row[c.key] ? <Link to={personPath(row[c.key])}>{row[c.key]}</Link> : '—')
        : DATE_COLUMNS.has(c.key)
          ? (row: any) => formatDateTime(row[c.key])
          : typeof (data?.items?.[0] || {})[c.key] === 'boolean'
            ? (row: any) => (row[c.key] ? 'Yes' : 'No')
            : undefined,
    };
  });

  const exportAll = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await downloadDrilldownCsv(filters, metric);
      downloadFile(`zippel-${metric}-${data?.period?.from}-to-${data?.period?.to}.csv`, blob);
    } catch (err) {
      setExportError(errorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <StatisticsPage
      title={TITLES[metric] || 'Rows'}
      subtitle="The rows behind the figure, under the same period and campus. Staff accounts are left out."
      filters={filters}
      onFiltersChange={(next) => {
        setPage(1);
        setFilters(next);
      }}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
      showComparison={false}
    >
      <section className="stats-section">
        <div className="stats-pager" style={{ marginTop: 0, marginBottom: 12 }}>
          <span>{formatCount(data?.total)} rows</span>
          <button type="button" className="stats-button stats-button-quiet" onClick={exportAll} disabled={exporting || !data?.total}>
            <Download size={14} aria-hidden="true" /> {exporting ? 'Exporting…' : 'Export all as CSV'}
          </button>
        </div>
        {exportError && <p className="stats-error">{exportError}</p>}
        <DataTable columns={columns} rows={data?.items || []} rowKey={(r: any) => r.id} />
        {(data?.totalPages || 1) > 1 && (
          <div className="stats-pager">
            <button type="button" className="stats-button stats-button-quiet" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {data.totalPages}
            </span>
            <button
              type="button"
              className="stats-button stats-button-quiet"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
        <p className="stats-note">Sorting a column sorts this page only; the export holds every row.</p>
      </section>
    </StatisticsPage>
  );
};

export default Drilldown;
